export interface LocalContrastResult {
  globalContrast: number;
  localContrastMean: number;
  mrzLocalContrast: number;
  score: number;
}

const GRID_COLS = 4;
const GRID_ROWS = 3;

/**
 * Splits the frame into a 4×3 grid and measures per-region luminance stddev.
 * MRZ-band cells are scored separately — washed-out text there is penalized more.
 */
export function analyzeLocalContrast(
  lums: Float32Array,
  w: number,
  h: number,
  mrzStartRow: number,
): LocalContrastResult {
  const cellW = Math.floor(w / GRID_COLS);
  const cellH = Math.floor(h / GRID_ROWS);

  let globalSum = 0,
    globalSumSq = 0;
  for (let i = 0; i < lums.length; i++) {
    globalSum += lums[i];
    globalSumSq += lums[i] * lums[i];
  }
  const globalMean = globalSum / lums.length;
  const globalContrast = Math.sqrt(
    Math.max(0, globalSumSq / lums.length - globalMean * globalMean),
  );

  let localContrastSum = 0;
  let mrzContrastSum = 0,
    mrzCellCount = 0;

  for (let gy = 0; gy < GRID_ROWS; gy++) {
    for (let gx = 0; gx < GRID_COLS; gx++) {
      let sum = 0,
        sumSq = 0,
        count = 0;
      for (let py = 0; py < cellH; py++) {
        for (let px = 0; px < cellW; px++) {
          const lum = lums[(gy * cellH + py) * w + (gx * cellW + px)];
          sum += lum;
          sumSq += lum * lum;
          count++;
        }
      }
      const mean = sum / count;
      const contrast = Math.sqrt(Math.max(0, sumSq / count - mean * mean));
      localContrastSum += contrast;

      if (gy * cellH >= mrzStartRow) {
        mrzContrastSum += contrast;
        mrzCellCount++;
      }
    }
  }

  const totalCells = GRID_COLS * GRID_ROWS;
  const localContrastMean = localContrastSum / totalCells;
  const mrzLocalContrast =
    mrzCellCount > 0 ? mrzContrastSum / mrzCellCount : 0;

  const localScore = Math.min(1, localContrastMean / 20);
  const mrzScore = Math.min(1, mrzLocalContrast / 15);
  const score = localScore * 0.6 + mrzScore * 0.4;

  return { globalContrast, localContrastMean, mrzLocalContrast, score };
}
